import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import ReportSummaryCards from '../../components/report/ReportSummaryCards';
import {
    formatMoney,
    formatDateTime,
    invoiceStatuses,
    statusBadgeClass,
} from '../../lib/reportHelpers';
import { AppLayout } from '../../components/AppLayout';
import { send } from '../../components';
import { useLoading } from '../../hooks/useLoading';
import { toast } from 'sonner';
import SimpleTable from '../../components/report/SimpleTable';
import SimplePagination from '../../components/report/SimplePagination';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
const defaultFilter = {
    id: '',
    merchant_ref: '',
    status: '',
    date_from: '',
    date_to: '',
};

const StatusBadge = ({ value }) => {
    const { t } = useTranslation();

    return (
        <div className="flex justify-center">
            <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-medium ${statusBadgeClass(value)}`}>
                {t(value || '-')}
            </span>
        </div>
    );
};

const InvoiceReport = () => {
    const [filters, setFilters] = useState(defaultFilter);
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({});
    const { showLoading } = useLoading();
    const [pager, setPager] = useState({ skip: 0, take: 20 });
    const [total, setTotal] = useState(0);
    const { t } = useTranslation();

    const payload = useMemo(() => ({
        ...filters,
        skip: pager.skip,
        take: pager.take,
        sort: [{ field: 'created_at', dir: 'desc' }],
    }), [filters, pager]);

    const getInvoiceReport = async (paramfilter) => {
        const res = await send("cu0907", paramfilter, showLoading);
        if (res.status === 'error') {
            toast.error(res.message);
            return;
        }
        setRows(res.data)
        setTotal(res.total)
    }

    const getInvoiceReportSummary = async (paramfilter) => {
        const res = await send("cu0908", paramfilter, showLoading);
        if (res.status === 'error') {
            toast.error(res.message);
            return;
        }
        setSummary(res)
    }

    const loadData = (customPayload = payload) => {
        getInvoiceReport(customPayload);

        const { skip, take, sort, ...summaryFilter } = customPayload;
        getInvoiceReportSummary(summaryFilter);
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pager.skip, pager.take]);

    const onSearch = async () => {
        const next = { skip: 0, take: pager.take };
        setPager(next);
        await loadData({
            ...filters,
            skip: 0,
            take: pager.take,
            sort: [{ field: 'created_at', dir: 'desc' }],
        });
    };

    const onReset = async () => {
        setFilters(defaultFilter);
        const nextPager = { skip: 0, take: 20 };
        setPager(nextPager);
        await loadData({
            ...defaultFilter,
            skip: 0,
            take: 20,
            sort: [{ field: 'created_at', dir: 'desc' }],
        });
    };
    const onExportExcel = async () => {
        const exportPayload = {
            ...filters,
            skip: 0,
            take: total || 999999,
            sort: [{ field: 'created_at', dir: 'desc' }],
        };

        const res = await send("cu0907", exportPayload, showLoading);
        if (res.status === 'error') {
            toast.error(res.message);
            return;
        }

        const exportRows = (res.data || []).map((row, index) => ({
            '№': index + 1,
            [t('ref')]: row.id ?? '',
            [t('amount')]: Number(row.amount || 0),
            [t('currency')]: row.currency ?? '',
            [t('numPeople')]: row.count_total ?? '',
            [t('fee')]: Number(row.fee_amount || 0),
            [t('totalAmountRpt')]: Number(row.amount_total || 0),
            [t('status')]: t(row.status || '-'),
            [t('merchantRef')]: row.merchant_ref ?? '',
            [t('description')]: row.txndesc ?? '',
            [t('CreatedAt')]: formatDateTime(row.created_at),
            [t('PaidAt')]: formatDateTime(row.paid_at),
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        worksheet['!cols'] = [
            { wch: 6 },
            { wch: 14 },
            { wch: 14 },
            { wch: 10 },
            { wch: 12 },
            { wch: 12 },
            { wch: 14 },
            { wch: 16 },
            { wch: 18 },
            { wch: 24 },
            { wch: 22 },
            { wch: 22 },
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoices');

        const excelBuffer = XLSX.write(workbook, {
            bookType: 'xlsx',
            type: 'array',
        });

        saveAs(
            new Blob([excelBuffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
            }),
            `invoice_report_${new Date().toISOString().slice(0, 10)}.xlsx`
        );
    };
    const summaryItems = [
        { title: t('totalInvoice'), value: Number(summary.total_count || 0) },
        { title: t('totalAmountRpt'), value: Number(summary.total_amount_total || 0), money: true },
        // { title: 'Нийт төлбөртэй дүн', value: Number(summary.total_amount || 0), money: true },
        // { title: 'Нийт шимтгэл', value: Number(summary.total_fee_amount || 0), money: true },
        { title: t('paid'), value: Number(summary.paid_count || 0) },
        { title: t('paidAmount'), value: Number(summary.paid_amount_total || 0), money: true },
        { title: t('pending'), value: Number(summary.pending_count || 0) },
        { title: t('pendingAmount'), value: Number(summary.pending_amount_total || 0), money: true },
        { title: t('canceled'), value: Number(summary.canceled_count || 0) },
        { title: t('expired'), value: Number(summary.expired_count || 0) },
    ];

    const columns = [
        { key: 'id', title: t('ref'), width: 20 },
        { key: 'amount', title: t('amount'), align: 'right', render: (row) => formatMoney(row.amount) },
        { key: 'count_total', title: t('numPeople'), width: 220 },
        { key: 'fee_amount', title: t('fee'), align: 'right', render: (row) => formatMoney(row.fee_amount) },
        { key: 'amount_total', title: t('totalAmountRpt'), align: 'right', render: (row) => formatMoney(row.amount_total) },
        { key: 'currency', title: t('currency') },
        // { key: 'merchant_display_name', title: 'Merchant', width: 220 },
        // { key: 'customer_name', title: 'Customer', width: 180 },
        // { key: 'cust_phone', title: 'Phone', width: 140 },
        { key: 'status', title: t('status'), align: 'center', render: (row) => <StatusBadge value={row.status} /> },
        { key: 'merchant_ref', title: t("merchantRef"), width: 180 },
        { key: 'txndesc', title: t('description'), width: 220 },
        { key: 'created_at', title: t('CreatedAt'), render: (row) => formatDateTime(row.created_at) },
        { key: 'paid_at', title: t('PaidAt'), render: (row) => formatDateTime(row.paid_at) },
        // { key: 'expire_at', title: 'Expire At', render: (row) => formatDateTime(row.expire_at) },
    ];

    return (
        <AppLayout>
            <div className="space-y-4 p-4">
                <div className="rounded-2xl border bg-white p-4 shadow-sm">
                    <div className="mb-4 text-lg font-semibold">{t('invoiceReport')}</div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
                        <div className="flex flex-col gap-1">
                            <label className="text-xs text-gray-500">{t('dateFrom')}</label>
                            <Input
                                type="date"
                                value={filters.date_from}
                                className={"w-auto h-auto min-h-[40px]"}
                                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                            />
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-xs text-gray-500">{t('dateTo')}</label>
                            <Input
                                type="date"
                                value={filters.date_to}
                                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                                className="w-auto h-auto min-h-[40px]"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-xs text-gray-500">{t("reference")}</label>
                            <Input
                                placeholder={t("ref")}
                                value={filters.id}
                                onChange={(e) => setFilters({ ...filters, id: e.target.value })}
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-xs text-gray-500">{t("merchantRef")}</label>
                            <Input
                                placeholder={"REF-001"}
                                value={filters.merchant_ref}
                                onChange={(e) => setFilters({ ...filters, merchant_ref: e.target.value })}
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-xs text-gray-500">{t("status")}</label>
                            <select
                                className="h-10 rounded-md border px-3"
                                value={filters.status}
                                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            >
                                {invoiceStatuses.map((x) => (
                                    <option key={x.value} value={x.value}>{t(x.label)}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                        <Button
                            onClick={onSearch}
                            className="w-full sm:w-auto"
                        >
                            {t('search')}
                        </Button>

                        <Button
                            variant="outline"
                            onClick={onReset}
                            className="w-full sm:w-auto"
                        >
                            {t('clear')}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={onExportExcel}
                            className="w-full sm:w-auto"
                        >
                            {t('exportExcel')}
                        </Button>
                    </div>
                </div>
                <ReportSummaryCards items={summaryItems} />
                <SimpleTable columns={columns} data={rows} />
                <SimplePagination
                    skip={pager.skip}
                    take={pager.take}
                    total={total}
                    // loading={loading}
                    onChange={setPager}
                />
            </div>
        </AppLayout >
    );
};

export default InvoiceReport;